import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { CheckCircle2, XCircle, AlertTriangle, ShieldCheck, ArrowLeft, Download } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function VerificarSolvenciaPage() {
  // Uso splat "*" para capturar nro con slash (ej: SOLV-00001/2026)
  const params = useParams();
  const nro = params["*"] || params.nro || "";

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const run = async () => {
      try {
        const r = await axios.get(`${API}/public/solvencia/verificar/${encodeURIComponent(nro)}`);
        setData(r.data);
      } catch (e) {
        setError(e.response?.data?.detail || "No se pudo verificar la solvencia.");
      } finally { setLoading(false); }
    };
    if (nro) run(); else { setLoading(false); setError("Falta el número de solvencia."); }
  }, [nro]);

  const descargar = async () => {
    if (!data?.id || !data?.share_token) return;
    const url = `${API}/public/solvencia/${data.id}/pdf?token=${data.share_token}`;
    const a = document.createElement("a");
    a.href = url; a.target = "_blank"; a.rel = "noopener";
    a.click();
  };

  const autentica = !!data?.autentica;
  const encontrada = !!data?.encontrada;

  return (
    <div className="min-h-screen relative overflow-hidden" style={{ background: "linear-gradient(160deg,#052e16 0%,#14532D 45%,#166534 100%)" }} data-testid="verificar-solvencia-page">
      {/* Patrón sutil de fondo */}
      <div
        className="absolute inset-0 opacity-[0.06] pointer-events-none"
        style={{
          backgroundImage: "radial-gradient(circle at 25px 25px, #FFF 2px, transparent 0)",
          backgroundSize: "50px 50px",
        }}
      />

      {/* Top nav minimal */}
      <nav className="relative z-10 px-6 py-5 flex items-center justify-between max-w-6xl mx-auto">
        <Link to="/" className="flex items-center gap-3 text-white/95 hover:text-orange-200 transition" data-testid="link-inicio-verify">
          <img src="/brand/icon_palmaplus.png" alt="Palma Plus" className="w-11 h-11 rounded-xl bg-white shadow-md object-contain p-0.5" />
          <div>
            <p className="text-[10px] font-bold uppercase tracking-widest text-orange-300">Verificación pública</p>
            <p className="font-display font-extrabold text-sm leading-tight">SISAUR · Saneamiento Urbano</p>
          </div>
        </Link>
        <Button variant="ghost" asChild className="text-white/85 hover:text-white hover:bg-white/10">
          <Link to="/"><ArrowLeft className="w-4 h-4 mr-2" />Inicio</Link>
        </Button>
      </nav>

      <div className="relative z-10 max-w-3xl mx-auto px-6 pt-6 pb-16">
        {loading && (
          <Card className="p-10 text-center border-0 bg-white/95 backdrop-blur shadow-2xl">
            <ShieldCheck className="w-12 h-12 text-green-700 animate-pulse mx-auto mb-3" />
            <p className="text-green-800 font-semibold">Verificando autenticidad…</p>
          </Card>
        )}

        {!loading && error && (
          <Card className="p-10 text-center border-0 bg-white/95 backdrop-blur shadow-2xl" data-testid="verify-error">
            <XCircle className="w-14 h-14 text-rose-600 mx-auto mb-4" />
            <h1 className="font-display font-extrabold text-2xl text-rose-900">Error al verificar</h1>
            <p className="text-slate-600 mt-2">{error}</p>
          </Card>
        )}

        {!loading && data && !encontrada && (
          <Card className="p-10 border-0 bg-white/95 backdrop-blur shadow-2xl" data-testid="verify-not-found">
            <div className="flex flex-col items-center text-center">
              <div className="w-20 h-20 rounded-full bg-rose-100 flex items-center justify-center mb-4">
                <XCircle className="w-11 h-11 text-rose-700" />
              </div>
              <h1 className="font-display font-extrabold text-3xl text-rose-900">Solvencia no encontrada</h1>
              <p className="text-slate-600 mt-2 max-w-md">
                {data.mensaje || "No existe una solvencia con este número en SISAUR."}
              </p>
              <p className="mt-4 text-xs text-slate-500 font-mono bg-slate-100 rounded-full px-3 py-1">N° consultado: {nro}</p>
            </div>
          </Card>
        )}

        {!loading && data && encontrada && (
          <>
            {/* Banner grande de resultado */}
            <Card className={`p-8 border-0 shadow-2xl text-white overflow-hidden relative`} style={{
              background: autentica
                ? "linear-gradient(135deg,#14532D 0%,#166534 55%,#65A30D 130%)"
                : "linear-gradient(135deg,#7F1D1D 0%,#B91C1C 55%,#EA580C 130%)",
            }} data-testid="verify-result">
              {/* Decorative circle */}
              <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full bg-white/5 pointer-events-none" />
              <div className="absolute -bottom-20 -left-10 w-56 h-56 rounded-full bg-white/5 pointer-events-none" />

              <div className="relative flex flex-col sm:flex-row items-start sm:items-center gap-5">
                <div className={`w-24 h-24 rounded-2xl flex items-center justify-center shrink-0 ${autentica ? "bg-emerald-400/30" : "bg-rose-300/25"}`}>
                  {autentica
                    ? <CheckCircle2 className="w-16 h-16 text-emerald-100" strokeWidth={2.2} />
                    : <XCircle className="w-16 h-16 text-rose-100" strokeWidth={2.2} />}
                </div>
                <div className="flex-1">
                  <p className="text-orange-200 text-[11px] font-bold uppercase tracking-[0.2em]">
                    {autentica ? "Solvencia auténtica" : (data.estatus === "Vencida" ? "Solvencia vencida" : (data.estatus === "Anulada" ? "Solvencia anulada" : "Solvencia no válida"))}
                  </p>
                  <h1 className="font-display font-extrabold text-4xl md:text-5xl leading-tight mt-1">
                    {autentica ? "VIGENTE ✓" : (data.estatus || "NO VÁLIDA").toUpperCase()}
                  </h1>
                  <p className="text-white/85 text-sm mt-3 max-w-xl">
                    {autentica
                      ? "Este documento fue emitido por SISAUR – Palma Plus, Saneamiento Urbano y su vigencia está confirmada al día de hoy."
                      : (data.estatus === "Vencida"
                        ? "Esta solvencia ya no se encuentra vigente. El contribuyente debe emitir una nueva."
                        : (data.motivo_anulacion || "Esta solvencia fue marcada como no válida en el sistema."))}
                  </p>
                </div>
              </div>
            </Card>

            {/* Detalle */}
            <Card className="p-6 md:p-8 mt-5 border-0 bg-white/95 backdrop-blur shadow-xl" data-testid="verify-detail">
              <h2 className="font-display font-bold text-lg text-green-900 flex items-center gap-2">
                <ShieldCheck className="w-5 h-5 text-orange-500" /> Detalle del certificado
              </h2>
              <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3 text-sm">
                <Field label="N° Solvencia" value={data.nro_solvencia} mono />
                <Field label="Estatus" value={data.estatus} />
                <Field label="Contribuyente" value={data.contribuyente_nombre} />
                <Field label="RIF / Documento" value={data.rif} mono />
                <Field label="Período cubierto" value={data.periodo_cubierto_label || data.periodo_cubierto} />
                <Field label="Fecha de emisión" value={data.fecha_emision} />
                <Field label="Válida hasta" value={data.fecha_vencimiento} highlight={autentica} />
              </div>

              {data.motivo_anulacion && (
                <div className="mt-5 flex gap-2 items-start p-3 rounded-lg bg-rose-50 border border-rose-200 text-sm text-rose-900">
                  <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                  <p><strong>Motivo de anulación:</strong> {data.motivo_anulacion}</p>
                </div>
              )}

              {data.id && data.share_token && (
                <div className="mt-6 flex flex-wrap gap-3">
                  <Button
                    onClick={descargar}
                    className="bg-orange-500 hover:bg-orange-600 text-white"
                    data-testid="btn-descargar-desde-verify"
                  >
                    <Download className="w-4 h-4 mr-2" /> Descargar PDF original
                  </Button>
                </div>
              )}
            </Card>

            <p className="text-center text-white/60 text-xs mt-6">
              Verificación oficial en línea · Palma Plus, Saneamiento Urbano · SISAUR
            </p>
          </>
        )}
      </div>
    </div>
  );
}

function Field({ label, value, mono, highlight }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">{label}</p>
      <p className={`mt-0.5 ${mono ? "font-mono" : ""} ${highlight ? "text-orange-700 font-bold" : "text-slate-900"} text-base`}>
        {value || "-"}
      </p>
    </div>
  );
}
